import cx from 'classnames';
import _ from 'lodash';
import React from 'react';
import ReactModal from 'react-modal';
import { Button } from '../button';
import { MultiSelectButton, MultiSelectButtonProps } from '../button/multi-select-button';
import { IconID } from '../icon';
import { LoadingIndicator } from 'packages/radiant-react/loading-indicator';
import { TooltipConfig, Typography } from 'packages/radiant-react/typography';
import styles from './modal.module.scss';

const DialogOpen = 'dialog-open';
const DialogClose = 'dialog-close';

ReactModal.setAppElement('body');
(ReactModal.defaultStyles.overlay as any).backgroundColor = 'rgba(28,35,48,.5)';

export enum ModalTestId {
  CONFIRM_BUTTON = 'modal-confirm-button',
  CROSS_BUTTON = 'modal-cross-button',
  DENY_BUTTON = 'modal-deny-button',
  DISMISS_BUTTON = 'modal-dismiss-button',
  CONTAINER = 'modal-container',
}

export enum ModalSize {
  XSMALL = 'xsmall',
  SMALL = 'small',
  MEDIUM = 'medium',
  MEDIUM3 = 'medium3',
  LARGE = 'large',
  FULLSCREEN = 'fullscreen',
  FULLPAGE = 'fullpage',
}

interface ModalButton {
  tooltip?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
}

export enum ModalType {
  Alert = 'alert',
  NoAlert = 'no-alert',
  Wizard = 'wizard',
}

export interface ConfirmButton extends ModalButton {
  text: string;
  onConfirm: () => void;
  className?: string;
}

export interface DenyButton extends ModalButton {
  text: string;
  onDeny: () => void;
  className?: string;
}

export interface DismissButton extends ModalButton {
  text: string;
  onClick?: () => void;
  className?: string;
}

export interface BackButton extends ModalButton {
  onBack: () => void;
}

export interface ModalProps {
  /* Used internally to calling alert type modal */
  variant?: ModalType;
  /* Core Visual Props */
  isOpen: boolean;
  size?: ModalSize;
  title?: string | React.ReactElement | React.ReactElement[];
  titleTooltip?: string;
  titleTooltipConfig?: TooltipConfig;
  // Hides the dialog header, if this flag is set to false
  includesHeader?: boolean;
  includesFooter?: boolean;
  children?: React.ReactElement | React.ReactElement[];
  autoScrollContent?: boolean;
  // Shows a loading state over the contents, and disables confirm/deny buttons
  isContentLoading?: boolean;
  contentLoadingText?: string;

  /* Button Props (some contain callbacks) */
  confirmButton?: ConfirmButton;
  /**
   * Use multi-select button for confirm button
   * For adding more than one options for confirmation.
   * Use confirmButton or multiSelectConfirmButton only not both
   */
  multiSelectConfirmButton?: MultiSelectButtonProps;
  denyButton?: DenyButton;
  dismissButton?: DismissButton;
  hasCrossButton?: boolean;
  backButton?: BackButton;

  /* Behaviours */
  doNotDismissOnOutsideClickOrEsc?: boolean;

  /* Top-Level Callbacks */
  // Called upon any interaction to dismiss the modal without taking any actions, such as
  // clicking the cancel button, clicking the X in the corner, pressing escape, etc
  onDismiss?: () => void;
  // Called after the modal is finished its closing logic and animation. You should only stop
  // rendering the component via JSX after this callback is called (doing so earlier may cause
  // the closing animation to get cut off abruptly).
  onAfterClose?: () => void;
  alwaysMaxHeight?: boolean;
  /**
   * Sets padding to 0 for modal content
   * @default false
   */
  noPadding?: boolean;

  /**
   * Additional styling for React Modal
   * @default ''
   */
  className?: string;
  /**
   * Additional styling for React Modal content
   * @default ''
   */
  contentClassName?: string;
  /**
   * if flag is true, modal content will fill all available space
   * @default false
   */
  isContentHeightDynamic?: boolean;
  /**
   * If flag is `true`, modal will scale for smaller screens.
   * - Review modal SCSS to know more about how this is done.
   *
   * P.S.: When set to `true`,
   * - Test all breakpoints to ensure the modal is usable.
   * - Even though breakpoints may not specify it, test upto `480px` height.
   * @default false
   */
  shouldScaleForSmallerScreens?: boolean;
  modalTestId?: string;
  /**
   * Styles for header section of the modal
   */
  customHeaderClassName?: string;
  /**
   * If primary button needs to be enabled while loading
   */
  enablePrimaryButton?: boolean;
  /**
   * For customizing the footer
   */
  customFooterClassName?: string;
  /**
   * For customizing the loading screen
   */
  customLoaderClassName?: string;
  /**
   * For styling the loading text shown initially
   */
  customLoadingTextClassName?: string;
  /**
   * For styling the react portal
   */
  customPortalClassName?: string;
  /**
   * For announcing the content of the modal to screen readers
   */
  contentLabel?: string;
}

export const Modal: React.FC<ModalProps> = ({
  variant = ModalType.NoAlert,
  isOpen,
  size = ModalSize.MEDIUM,
  title = '',
  titleTooltip = '',
  titleTooltipConfig,
  includesHeader = true,
  includesFooter = true,
  children,
  autoScrollContent = true,
  isContentLoading,
  contentLoadingText,
  confirmButton,
  multiSelectConfirmButton,
  dismissButton,
  denyButton,
  hasCrossButton = false,
  backButton,
  doNotDismissOnOutsideClickOrEsc = false,
  onDismiss = _.noop, // TODO(Rifdhan) can we not use defaults?
  onAfterClose = _.noop,
  alwaysMaxHeight = false,
  noPadding = false,
  className = '',
  contentClassName = '',
  isContentHeightDynamic = false,
  shouldScaleForSmallerScreens = false,
  modalTestId,
  customHeaderClassName = '',
  enablePrimaryButton = false,
  customFooterClassName = '',
  customLoaderClassName = '',
  customLoadingTextClassName = '',
  customPortalClassName = '',
  contentLabel = '',
}: ModalProps) => {
  const isModalWizard = variant === ModalType.Wizard;
  const isModalAlert = variant === ModalType.Alert;
  const [embedClass, setEmbedClass] = React.useState<ReactModal.Styles>({});
  const [openModal, setOpenModal] = React.useState<boolean>(isOpen);
  React.useEffect(() => {
    // const asyncFunc = async () => {
    //     try {
    //         const style: React.CSSProperties = {};
    //             const embedModalCenter = await dispatchEmbedEvent(
    //                 blinkConstants.embedEvents.EmbedIframeCenter,
    //                 {},
    //             );
    //         const top = Math.max(0, embedModalCenter.data.iframeScrolled);
    //         const bottom =
    //             embedModalCenter.data.iframeHeight -
    //             embedModalCenter.data.iframeVisibleViewPort -
    //             top;
    //         style.paddingTop = top;
    //         style.paddingBottom = bottom;
    //         setEmbedClass({ overlay: style });
    //     } catch (error) {
    //         console.error(error);
    //     } finally {
    //         setOpenModal(isOpen);
    //     }
    // };
    setOpenModal(isOpen);
  }, [isOpen]);

  const renderHeader = () => {
    const typographyTitleVariant = isModalAlert ? 'section-label' : 'modal-title';

    return (
      <div className={cx(styles.header, customHeaderClassName)}>
        {backButton && (
          <Button
            className={styles.backBtn}
            type="secondary"
            size="m"
            icon={IconID.ARROW_LEFT}
            onClick={backButton.onBack}
            data-tooltip-content="Back"
          />
        )}
        <Typography
          variant={'modal-title'}
          ellipsis={{ rows: 1 }}
          tooltipConfig={{
            disableTooltip: titleTooltipConfig?.disableTooltip ?? false,
            showTooltipOnlyOnEllipsis: titleTooltipConfig?.showTooltipOnlyOnEllipsis ?? true,
            dataTooltipContent: titleTooltipConfig?.dataTooltipContent ?? title,
          }}
          noMargin
        >
          {title}
        </Typography>
        {titleTooltip && (
          <Button
            className={styles.infoIcon}
            icon={IconID.INFORMATION_CIRCLE}
            data-tooltip-content={titleTooltip}
            type="secondary"
            size="s"
          />
        )}
      </div>
    );
  };

  const footer = (
    <div className={cx(styles.footer, styles[variant], customFooterClassName)}>
      <div className={styles.denyBtnWrapper}>
        {!isModalAlert && denyButton && (
          <Button
            type="tertiary"
            isDisabled={isContentLoading || denyButton.isDisabled}
            onClick={denyButton.onDeny}
            text={denyButton.text}
            data-tooltip-content={denyButton.tooltip}
            isSaving={denyButton.isLoading}
            data-testid={ModalTestId.DENY_BUTTON}
            className={denyButton.className}
          />
        )}
      </div>
      {dismissButton && (
        <Button
          className={cx(styles.actionBtn, dismissButton.className)}
          type="secondary"
          isDisabled={dismissButton.isDisabled}
          onClick={dismissButton.onClick || onDismiss}
          text={dismissButton.text}
          data-tooltip-content={dismissButton.tooltip}
          isSaving={dismissButton.isLoading}
          data-testid={ModalTestId.DISMISS_BUTTON}
        />
      )}
      {confirmButton && (
        <Button
          className={cx(styles.actionBtn, confirmButton.className)}
          type="primary"
          isDisabled={enablePrimaryButton ? false : isContentLoading || confirmButton.isDisabled}
          onClick={confirmButton.onConfirm}
          text={confirmButton.text}
          data-tooltip-content={confirmButton.tooltip}
          isSaving={confirmButton.isLoading}
          data-testid={ModalTestId.CONFIRM_BUTTON}
        />
      )}
      {multiSelectConfirmButton && (
        <MultiSelectButton className={styles.actionBtn} {...multiSelectConfirmButton} />
      )}
    </div>
  );

  const renderChildren = () => {
    const contentClasses = cx(styles.content, styles[variant], contentClassName, {
      [styles.autoScrollContent]: autoScrollContent,
      [styles.noPadding]: noPadding,
      [styles.contentWithHeaderAbove]: isModalAlert && title,
      [customLoaderClassName]: isContentLoading,
    });

    return (
      <LoadingIndicator.Global
        hideLoader={!isContentLoading}
        className={styles.loadingWrapper}
        loadingTextClassName={customLoadingTextClassName}
        loadingText={contentLoadingText ?? ''}
      >
        <div className={contentClasses}>{children}</div>
      </LoadingIndicator.Global>
    );
  };

  return (
    <ReactModal
      isOpen={openModal}
      shouldCloseOnOverlayClick={!doNotDismissOnOutsideClickOrEsc}
      shouldCloseOnEsc={!doNotDismissOnOutsideClickOrEsc}
      ariaHideApp={false}
      // This must match the transition time value for ReactModal__Overlay in the CSS
      closeTimeoutMS={200}
      className={cx(
        className,
        styles.modal,
        styles[variant],
        isModalAlert ? styles[ModalSize.SMALL] : styles[size],
        {
          [styles.alwaysMaxHeight]: alwaysMaxHeight,
          [styles.isModalWizard]: isModalWizard,
          [styles.isContentHeightDynamic]: isContentHeightDynamic,
          [styles.shouldScaleForSmallerScreens]: shouldScaleForSmallerScreens,
        }
      )}
      style={embedClass}
      onRequestClose={onDismiss}
      onAfterClose={onAfterClose}
      testId={modalTestId ?? ModalTestId.CONTAINER}
      portalClassName={cx('ReactModalPortal', customPortalClassName)}
      contentLabel={contentLabel}
    >
      {includesHeader && renderHeader()}
      {!isModalAlert && hasCrossButton && (
        <Button
          className={styles.crossBtn}
          icon={IconID.CROSS}
          onClick={onDismiss}
          ariaLabel="Close Modal"
          type="tertiary"
          data-testid={ModalTestId.CROSS_BUTTON}
        />
      )}
      {children && renderChildren()}
      {includesFooter && footer}
    </ReactModal>
  );
};
